/**
 * Community statistics calculation logic
 * Shared between web and mobile applications
 */

/**
 * Calculates community question percentage and difficulty level
 * @param {Object} communityData - Community data for the question
 * @param {number} communityData.times_answered - Total times question was answered
 * @param {number} communityData.times_answered_correct - Times answered correctly
 * @returns {Object} Community statistics with color, percentage, message, and level
 */
export const calculateCommunityStats = (communityData) => {
    if (!communityData || !communityData.times_answered || communityData.times_answered === 0) {
        return {
            color: '#ef4444', // Red for unknown difficulty
            percentage: 0,
            message: 'This question appears to be challenging for most users.',
            level: 'hard'
        };
    }

    const percentage = (communityData.times_answered_correct / communityData.times_answered) * 100;

    if (isNaN(percentage) || !isFinite(percentage) || percentage < 50) {
        return {
            color: '#ef4444', // Red
            percentage: Math.round(percentage) || 0,
            message: 'This question appears to be challenging for most users.',
            level: 'hard'
        };
    } else if (percentage < 80) {
        return {
            color: '#f59e0b', // Yellow/Orange
            percentage: Math.round(percentage),
            message: 'This question has moderate difficulty.',
            level: 'medium'
        };
    } else {
        return {
            color: '#10b981', // Green
            percentage: Math.round(percentage),
            message: 'Most users answer this question correctly.',
            level: 'easy'
        };
    }
};

/**
 * Gets community difficulty level from percentage
 * @param {number} percentage - Success percentage (0-100)
 * @returns {string} Difficulty level: 'easy', 'medium', or 'hard'
 */
export const getDifficultyLevel = (percentage) => {
    if (percentage >= 80) return 'easy';
    if (percentage >= 50) return 'medium';
    return 'hard';
};

/**
 * Gets color for difficulty level
 * @param {string} level - Difficulty level: 'easy', 'medium', or 'hard'
 * @returns {string} Hex color code
 */
export const getDifficultyColor = (level) => {
    switch (level) {
        case 'easy':
            return '#10b981'; // Green
        case 'medium':
            return '#f59e0b'; // Yellow/Orange
        case 'hard':
        default:
            return '#ef4444'; // Red
    }
};

/**
 * Gets message for difficulty level
 * @param {string} level - Difficulty level: 'easy', 'medium', or 'hard'
 * @returns {string} User-friendly message
 */
export const getDifficultyMessage = (level) => {
    switch (level) {
        case 'easy':
            return 'Most users answer this question correctly.';
        case 'medium':
            return 'This question has moderate difficulty.';
        case 'hard':
        default:
            return 'This question appears to be challenging for most users.';
    }
};

/**
 * Formats community statistics for display
 * @param {Object} communityData - Raw community data
 * @returns {Object} Formatted statistics for UI display
 */
export const formatCommunityStatsForDisplay = (communityData) => {
    const stats = calculateCommunityStats(communityData);

    return {
        ...stats,
        displayText: `${stats.percentage}% correct`,
        difficultyText: `${stats.level.charAt(0).toUpperCase() + stats.level.slice(1)} Question`,
        statsText: communityData?.times_answered
            ? `${communityData.times_answered} users attempted`
            : 'No community data available'
    };
};